import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import apiConfig from "../../../../apiConfig";

const ResellerProducts = () => {
  const [products, setProducts] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchResellerId, setSearchResellerId] = useState("");

  useEffect(() => {
    fetchProducts();
    fetchResellers();
  }, []);

  // ===============================
  // FETCH ALL PRODUCTS
  // ===============================
  const fetchProducts = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${apiConfig.baseUrl}${apiConfig.getAllReSellerProducts}`,
      );

      if (res.data?.success) {
        setProducts(res.data.products || []);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error("Fetch reseller products error:", error);
      toast.error("Failed to fetch reseller products");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // ===============================
  // FETCH RESELLERS FOR DROPDOWN
  // ===============================
  const fetchResellers = async () => {
    try {
      const res = await axios.get(
        `${apiConfig.baseUrl}${apiConfig.GetAllReseller}`,
      );

      setResellers(res.data.data || []);
    } catch (error) {
      console.error("Fetch resellers error:", error);
      setResellers([]);
    }
  };

  // ===============================
  // FILTER PRODUCTS BY RESELLER
  // ===============================
  const filterProducts = async (resellerUniqueId) => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${apiConfig.baseUrl}${apiConfig.filterReSellerProducts}?resellerUniqueId=${resellerUniqueId}`,
      );

      if (res.data?.success) {
        setProducts(res.data.products || []);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error("Filter reseller products error:", error);
      toast.error("Failed to filter reseller products");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // ===============================
  // HANDLE DROPDOWN CHANGE
  // ===============================
  const handleSearchChange = (value) => {
    setSearchResellerId(value);

    if (!value.trim()) {
      fetchProducts();
    } else {
      filterProducts(value);
    }
  };

  // ===============================
  // CLEAR SEARCH
  // ===============================
  const handleClearSearch = () => {
    setSearchResellerId("");
    fetchProducts();
  };

  // ===============================
  // REFRESH
  // ===============================
  const handleRefresh = () => {
    setSearchResellerId("");
    fetchProducts();
  };

  return (
    <div className="p-4 md:p-6">
      {/* ================= HEADER ================= */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold">Reseller Uploaded Products</h2>

        <div className="flex flex-wrap items-end gap-3">
          {/* RESELLER DROPDOWN */}
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-700 mb-1">
              Select Reseller
            </label>

            <select
              value={searchResellerId}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-72 border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-blue-500"
            >
              <option value="">Search Reseller Product</option>

              {resellers?.map((reseller) => (
                <option key={reseller._id} value={reseller.resellerId}>
                  {reseller.resellerId || "-"} - {reseller.fullName || "-"}
                </option>
              ))}
            </select>
          </div>

          {/* REMOVE */}
          <button
            onClick={handleClearSearch}
            className="px-4 py-2 border border-gray-300 rounded-md bg-gray-100 hover:bg-gray-200 text-sm"
          >
            Remove
          </button>

          {/* REFRESH */}
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* ================= MAIN CARD ================= */}
      <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
        {/* ================= LOADER ================= */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="h-12 w-12 rounded-full border-b-2 border-blue-600 animate-spin" />
          </div>
        )}

        {/* ================= NO DATA ================= */}
        {!loading && products.length === 0 && (
          <div className="text-center py-20">
            <h3 className="text-lg font-medium text-gray-400">
              No Products Found
            </h3>

            <p className="text-sm text-gray-500 mt-2">
              Reseller uploaded products will appear here
            </p>
          </div>
        )}

        {/* ================= TABLE ================= */}
        {!loading && products.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1250px]">
              <thead className="bg-gray-100">
                <tr className="text-left text-sm">
                  <th className="p-3">Reseller Name</th>
                  <th className="p-3">Reseller ID</th>
                  <th className="p-3">Shop Name</th>
                  <th className="p-3">Product Title</th>
                  <th className="p-3">Description</th>
                  <th className="p-3">Category</th>
                  <th className="p-3">Brand</th>
                  <th className="p-3">Images</th>
                  <th className="p-3">Base Price</th>
                  <th className="p-3">Final Price</th>
                  <th className="p-3">Earning</th>
                  <th className="p-3">Stock</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>

              <tbody>
                {products.map((item) => {
                  const product = item.productId || {};
                  const reseller = item.reseller || {};

                  const earning =
                    Number(item.finalPrice || 0) - Number(item.basePrice || 0);

                  return (
                    <tr
                      key={item._id}
                      className="border-b text-sm hover:bg-gray-50"
                    >
                      {/* RESELLER NAME */}
                      <td className="p-3 font-medium">
                        {reseller.fullName || "N/A"}
                      </td>

                      {/* RESELLER ID */}
                      <td className="p-3">{reseller.resellerId || "N/A"}</td>

                      {/* SHOP NAME */}
                      <td className="p-3">{reseller.shopName || "N/A"}</td>

                      {/* PRODUCT TITLE */}
                      <td className="p-3 font-medium">
                        {product.title || "N/A"}
                      </td>

                      {/* DESCRIPTION */}
                      <td className="p-3 max-w-[250px]">
                        {product.description
                          ? product.description.length > 70
                            ? product.description.substring(0, 70) + "..."
                            : product.description
                          : "N/A"}
                      </td>

                      {/* CATEGORY */}
                      <td className="p-3">{product.category || "N/A"}</td>

                      {/* BRAND */}
                      <td className="p-3">{product.brand || "N/A"}</td>

                      {/* IMAGES */}
                      <td className="p-3">
                        <div className="flex gap-2 flex-wrap">
                          {[
                            product.productimage1,
                            product.productimage2,
                            product.productimage3,
                            product.productimage4,
                          ]
                            .filter(Boolean)
                            .map((img, index) => (
                              <img
                                key={index}
                                src={`https://www.bharatcod.com/${img}`}
                                alt="product"
                                className="w-14 h-14 object-cover rounded border"
                              />
                            ))}
                        </div>
                      </td>

                      {/* BASE PRICE */}
                      <td className="p-3 font-medium">
                        ₹{item.basePrice || 0}
                      </td>

                      {/* FINAL PRICE */}
                      <td className="p-3 font-medium">
                        ₹{item.finalPrice || 0}
                      </td>

                      {/* EARNING */}
                      <td className="p-3 font-semibold text-green-600">
                        ₹{earning}
                      </td>

                      {/* STOCK */}
                      <td className="p-3">{product.stock || 0}</td>

                      {/* STATUS ONLY VIEW */}
                      <td className="p-3">
                        <span
                          className={`px-3 py-1 rounded-lg text-xs font-medium ${
                            item.approvalStatus === "approved"
                              ? "bg-green-100 text-green-700"
                              : item.approvalStatus === "rejected"
                                ? "bg-red-100 text-red-700"
                                : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {item.approvalStatus || "Pending"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResellerProducts;
